import { useState } from "react";
import { useApi } from "@excellent/ui/api";
import { formatDateTime } from "@excellent/ui";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import { resolveStoredVerdict } from "@excellent/core/verification-verdict-law";
import { href } from "../../nav";
import {
  canonicalEnvironmentAvailable,
  useCanonicalEnvironmentOptional,
} from "../../canonical-environment-provider";
import { ButtonRow, Callout, Field, Fields, Mono, Section, ToneBadge, Verdict } from "../../ui";
import { PageError, PageFrame, PagePending, type VerificationDetailPageProps } from "../page-frame";
import { receiptFailureGuidance } from "./failure-guidance";

/** Router params are normally decoded already; tolerate a still-encoded deep link. */
export function decodedReceiptId(id: string) {
  try { return decodeURIComponent(id); } catch { return id; }
}

export function SignedReceiptDetailPage({ id, now = new Date().toISOString() }: VerificationDetailPageProps & { now?: string }) {
  const canonical = useCanonicalEnvironmentOptional();
  const canonicalReady = canonicalEnvironmentAvailable(canonical, Date.parse(now));
  const subject = canonicalReady ? canonical?.response?.presentation?.subject ?? null : null;
  const enabled = Boolean(subject);
  const detail = useApi(
    universalVerification.receipt,
    enabled && subject ? { params: { id }, query: subject } : null,
  );
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState("");
  async function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshMessage("");
    try {
      await detail.mutate();
      setRefreshMessage("Receipt refreshed. Open Work and repair history to check for a newer result.");
    } catch {
      setRefreshMessage("Could not refresh. Check your connection and try again; this signed receipt has not been changed.");
    } finally {
      setRefreshing(false);
    }
  }
  if (!enabled) {
    return (
      <PageFrame page="receipt" title="Signed receipt">
        <Callout tone="unknown" title="Environment context required">
          This receipt cannot be read until the selected Environment is authorized and its current
          location is confirmed.
        </Callout>
        <a className="underline" href={href("receipts")}>Back to proof</a>
      </PageFrame>
    );
  }
  if (!detail.data && !detail.error) {
    return <PageFrame page="receipt" title="Signed receipt"><PagePending title="Loading signed receipt">Reading the verdict and its bound evidence for the selected Environment.</PagePending></PageFrame>;
  }
  if (detail.error || !detail.data || detail.data.receipt.payload.id !== id) {
    return <PageFrame page="receipt" title="Signed receipt">
      <a className="underline" href={href("receipts")}>Back to proof</a>
      <PageError title="Signed receipt could not load" error={detail.error ?? new Error("Receipt identity did not match the requested record.")} onRetry={() => void detail.mutate()} />
    </PageFrame>;
  }
  const { receipt, assurance, contract, attempt, evidence } = detail.data;
  const payload = receipt.payload;
  const resolved = resolveStoredVerdict({ verdict: payload, now });
  const artifacts = evidence.submission.artifacts;
  const needsAttention = payload.determinations.some(row => row.status !== "PASS");
  return <PageFrame page="receipt" wide title={contract?.content.title ?? "Signed receipt"} lede={`Signed receipt · ${formatDateTime(payload.issuedAt)}`}>
    <ButtonRow>
      <Verdict status={resolved.status} />
      <ToneBadge tone="neutral">{payload.assurance}</ToneBadge>
      <ToneBadge tone="neutral">{payload.determinations.length} obligations</ToneBadge>
      <ToneBadge tone="neutral">{payload.replay.status}</ToneBadge>
    </ButtonRow>
    <nav aria-label="Receipt evidence" className="flex flex-wrap gap-4 type-body">
      <a className="underline" href={href("receipts")}>All signed verdicts</a>
      <a className="underline" href={href("work-ledger", encodeURIComponent(payload.binding.workItemId))}>Work and repair history</a>
      <button type="button" className="underline" onClick={() => document.getElementById("receipt-evidence")?.scrollIntoView({ block: "start" })}>Evidence artifacts ({artifacts.length})</button>
      <button type="button" className="underline disabled:opacity-50" disabled={refreshing} onClick={() => void refresh()}>{refreshing ? "Refreshing…" : "Refresh receipt"}</button>
    </nav>
    {refreshMessage && <p role="status" className="type-body">{refreshMessage}</p>}
    {needsAttention && <section aria-label="Next step" className="min-w-0 rounded-lg border border-amber-300 bg-amber-50 p-5 text-amber-950">
      <h2 className="type-body font-semibold">Next: resolve the check that needs attention</h2>
      <p className="mt-2 type-body">Follow the guidance below and share this receipt with your worker or verification operator. A retry creates a separate result; this receipt remains the record of this check.</p>
      <a className="mt-3 inline-block rounded-md border border-amber-400 px-3 py-2 type-body font-medium underline" href={href("work-ledger", encodeURIComponent(payload.binding.workItemId))}>Check Work for a repair or newer result →</a>
    </section>}
    {needsAttention ? <details className="min-w-0 rounded-lg border px-4 py-2 type-subtext">
      <summary className="cursor-pointer py-2">Receipt authority details</summary>
      <p className="mt-2 break-words">{assurance.displayLabel}. {assurance.currentReceiptEligible
        ? "The verifier reports this receipt as eligible completion evidence."
        : "This record does not carry current completion authority."}</p>
      <p className="my-2 break-all">{assurance.blockedReasons.join(" · ")}</p>
    </details> : <Callout tone={assurance.currentReceiptEligible ? "neutral" : "unknown"} title={assurance.displayLabel}>
      {assurance.currentReceiptEligible
        ? "The canonical verifier reports this receipt as eligible completion evidence for the bound work and attempt."
        : `This record does not carry current completion authority: ${assurance.blockedReasons.join(", ") || "not eligible"}.`}
    </Callout>}
    <Section title="Obligations and evidence" note="Each result is bound to this signed receipt.">
      <div className="space-y-3">
        {payload.determinations.map(determination => {
          const obligation = contract?.content.obligations.find(row => row.id === determination.obligationId);
          const guidance = receiptFailureGuidance(determination.status, determination.reasonCodes);
          return <article key={determination.obligationId} className="min-w-0 rounded-lg border p-4" data-receipt-obligation={determination.obligationId}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h3 className="min-w-0 flex-1 break-words type-body font-medium">{obligation?.predicate.description ?? determination.obligationId}</h3>
              <Verdict status={determination.status} />
            </div>
            {guidance && <div className="mt-3 space-y-2 type-body" data-receipt-failure-guidance="">
              <p>{guidance.explanation}</p>
              <p><strong>What to do: </strong>{guidance.action}</p>
            </div>}
            <div className="mt-2 flex flex-wrap gap-3 type-body">
              {determination.evidenceArtifactIds.map(artifactId => <button type="button" key={artifactId} className="underline" onClick={() => document.getElementById(encodeURIComponent(artifactId))?.scrollIntoView({ block: "start" })}>Inspect {artifacts.find(row => row.id === artifactId)?.kind ?? "evidence artifact"}</button>)}
            </div>
            <details className="mt-3 type-subtext"><summary className="cursor-pointer py-2">Technical details and obligation reference</summary>
              <p className="mt-2 break-all">{determination.reasonCodes.join(" · ") || "No reason code recorded."}</p>
              <p className="mt-2 break-all">{determination.obligationId}</p>
            </details>
          </article>;
        })}
      </div>
    </Section>
    <Section title="Attempt and replay">
      <Fields>
        <Field label="Worker"><span className="break-all">{attempt?.worker.actorId ?? "Attempt parent unavailable"}</span></Field>
        <Field label="Attempt"><span className="break-all">{payload.binding.attemptId}</span></Field>
        <Field label="Execution boundary"><span className="break-all">{attempt?.worker.executionBoundaryId ?? "Unavailable"}</span></Field>
        <Field label="Replay">{payload.replay.status}</Field>
        <Field label="Replay commands"><span className="break-all">{payload.replay.commands.join("; ") || "No command recorded"}</span></Field>
        <Field label="Supersedes">{payload.supersedesReceiptId ? <a className="break-all underline" href={href("receipts", encodeURIComponent(payload.supersedesReceiptId))}>{payload.supersedesReceiptId}</a> : "No predecessor recorded"}</Field>
      </Fields>
    </Section>
    <div id="receipt-evidence" className="scroll-mt-4">
      <Section title="Evidence artifacts" note="The admitted content and digests retained with this receipt.">
        <div className="space-y-3">
          {artifacts.map(artifact => <article id={encodeURIComponent(artifact.id)} key={artifact.id} className="min-w-0 scroll-mt-4 rounded-lg border p-4">
            <h3 className="type-body font-medium">{artifact.kind}</h3>
            <p className="mt-1 break-all type-subtext">{artifact.id}</p>
            <p className="mt-1 break-all type-subtext">{artifact.contentDigest}</p>
            <details className="mt-3"><summary className="cursor-pointer type-body">Inspect retained content</summary>
              <pre className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap break-all rounded border p-3 type-subtext">{JSON.stringify(artifact.inlineContent, null, 2) ?? "No inline content retained."}</pre>
            </details>
          </article>)}
        </div>
      </Section>
    </div>
    <details className="rounded-lg border p-4">
      <summary className="cursor-pointer type-body font-medium">Signature and binding references</summary>
      <div className="mt-3 break-all type-subtext space-y-2">
        <p>Receipt <Mono>{payload.id}</Mono></p>
        <p>Payload digest <Mono>{receipt.payloadDigest}</Mono></p>
        <p>Signed by <Mono>{receipt.signature.keyId} · {receipt.signature.algorithm}</Mono></p>
        <p>Contract <Mono>{payload.binding.contractDigest}</Mono></p>
        <p>Evidence graph <Mono>{payload.binding.evidenceGraphDigest}</Mono></p>
        <p>This view displays the server-provided signed record; it does not perform a separate offline signature verification.</p>
      </div>
    </details>
  </PageFrame>;
}
